import { Component } from '@theme/component';

/**
 * @typedef {object} ReviewsPageRefs
 * @property {HTMLElement} list - The review grid
 * @property {HTMLElement} average - Big average number
 * @property {HTMLElement} summaryRating - Stars next to the average
 * @property {HTMLElement} count - "Based on [count] reviews"
 * @property {HTMLElement} levels - Rating breakdown rows
 * @property {HTMLElement} recommend - "[percent]% of reviewers recommend us"
 * @property {HTMLSelectElement} sort - Sort menu
 * @property {HTMLButtonElement} more - "Show more reviews"
 * @property {HTMLElement} status - Screen reader status
 * @property {HTMLElement} empty - Shown when nothing qualifies
 * @property {HTMLTemplateElement} cardTemplate - Markup for one review card
 * @property {HTMLElement} [filters] - Product filter buttons
 */

/**
 * @typedef {object} OkendoReview
 * @property {string} [body]
 * @property {string} [title]
 * @property {number} [rating]
 * @property {string} [languageCode]
 * @property {string} [dateCreated]
 * @property {string} [productName]
 * @property {string} [productUrl]
 * @property {string} [productImageUrl]
 * @property {{ body?: string }} [reply]
 * @property {{ displayName?: string, isVerified?: boolean }} [reviewer]
 */

const API = 'https://api.okendo.io/v1';
// Okendo pages hold reviews in every language; ask for big pages so one request usually fills a load
const FETCH_LIMIT = 50;
// Stop paging after this many requests in one load, even if few reviews qualified
const MAX_REQUESTS = 4;
const SORTS = ['date desc', 'rating desc', 'rating asc', 'helpful desc'];
// "Most recent" hides reviews below this many stars
const MIN_RECENT_RATING = 4;
// Okendo's default titles add nothing ("5 Stars"), so those cards go without one
const GENERIC_TITLE = /^\s*\d\s*stars?\s*$/i;

/**
 * Reviews page: fills the summary and the review grid from Okendo, with product filters, sorting and
 * "Show more". Layout is plain CSS; this script only loads data and swaps cards.
 *
 * @extends {Component<ReviewsPageRefs>}
 */
class ReviewsPageComponent extends Component {
  requiredRefs = ['list', 'average', 'summaryRating', 'count', 'levels', 'recommend', 'sort', 'more', 'status', 'empty', 'cardTemplate'];

  /** @type {OkendoReview[]} Qualifying reviews fetched but not shown yet */
  #buffer = [];
  /** @type {string | null} Okendo's next page for the current product and sort */
  #nextUrl = null;
  #shown = 0;
  // Bumped on every filter or sort change, so late responses for the old view are dropped
  #generation = 0;
  #summaryGeneration = 0;
  #productId = '';
  #started = false;

  static get observedAttributes() {
    return ['data-product-id'];
  }

  /**
   * A product page that swaps in another product in place (a linked product: variant-picker.js) morphs this
   * section's product id: load that product's reviews.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && this.#started && oldValue !== newValue) this.#select(null);
  }

  connectedCallback() {
    super.connectedCallback();
    // Reconnecting (e.g. the editor moving the section) keeps what's already rendered
    if (this.#started) return;
    this.#started = true;

    const params = new URLSearchParams(window.location.search);
    const handle = params.get('product');
    const button = handle ? this.#filterButtons.find((item) => item.dataset.handle === handle) : null;
    const sort = params.get('sort');
    if (sort && SORTS.includes(sort)) this.refs.sort.value = sort;
    this.#select(button ?? null);
  }

  get #storeUrl() {
    return `${API}/stores/${encodeURIComponent(this.dataset.subscriberId || '')}`;
  }

  /** @returns {HTMLButtonElement[]} */
  get #filterButtons() {
    return this.refs.filters ? [...this.refs.filters.querySelectorAll('button')] : [];
  }

  /* ---------- Controls ---------- */

  /** @param {Event} event */
  filter(event) {
    const button = /** @type {HTMLButtonElement} */ (event.target);
    if (button.getAttribute('aria-pressed') === 'true') return;
    this.#select(button);
    this.#updateUrl();
  }

  sort() {
    this.#reload();
    this.#updateUrl();
  }

  more() {
    this.#loadPage();
  }

  /** @param {HTMLButtonElement | null} button */
  #select(button) {
    const buttons = this.#filterButtons;
    const active = button ?? buttons[0] ?? null;
    for (const item of buttons) item.setAttribute('aria-pressed', String(item === active));
    // "All products", or no filters at all, falls back to the page's own product (product pages)
    this.#productId = active?.dataset.productId || this.dataset.productId || '';
    this.#loadSummary();
    this.#reload();
  }

  #updateUrl() {
    const url = new URL(window.location.href);
    const active = this.#filterButtons.find((item) => item.getAttribute('aria-pressed') === 'true');
    if (active?.dataset.handle) url.searchParams.set('product', active.dataset.handle);
    else url.searchParams.delete('product');
    if (this.refs.sort.value !== SORTS[0]) url.searchParams.set('sort', this.refs.sort.value);
    else url.searchParams.delete('sort');
    window.history.replaceState(window.history.state, '', url);
  }

  /* ---------- Data ---------- */

  /**
   * @param {string} url
   * @returns {Promise<any>}
   */
  #fetchJson(url) {
    // No custom headers: Okendo's CORS preflight rejects them
    return fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  get #scopeUrl() {
    return this.#productId ? `${this.#storeUrl}/products/shopify-${encodeURIComponent(this.#productId)}` : this.#storeUrl;
  }

  async #loadSummary() {
    if (!this.dataset.subscriberId) return;
    const generation = ++this.#summaryGeneration;
    const data = await this.#fetchJson(`${this.#scopeUrl}/review_aggregate`);
    // A newer filter has started its own summary
    if (generation !== this.#summaryGeneration) return;
    const aggregate = data?.reviewAggregate;
    if (!aggregate) return;

    const count = Number(aggregate.reviewCount ?? aggregate.ratingAndReviewCount) || 0;
    const total = Number(aggregate.reviewRatingValuesTotal ?? aggregate.ratingAndReviewValuesTotal) || 0;
    const levels = aggregate.reviewCountByLevel ?? aggregate.ratingAndReviewCountByLevel ?? {};
    const locale = this.dataset.locale || undefined;
    const average = count ? Math.round((total / count) * 10) / 10 : 0;
    const { average: averageNode, summaryRating, count: countNode, recommend } = this.refs;

    averageNode.textContent = average.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    summaryRating.setAttribute('aria-label', this.#ratingLabel(average));
    this.#fillStars(summaryRating, Math.round(average), average);
    countNode.textContent = (this.dataset.countText || '[count]').replace('[count]', count.toLocaleString(locale));

    for (const row of this.refs.levels.querySelectorAll('[data-level]')) {
      const level = Number(/** @type {HTMLElement} */ (row).dataset.level);
      const levelCount = Number(levels[`level${level}Count`]) || 0;
      const share = count ? levelCount / count : 0;
      /** @type {HTMLElement} */ (row).style.setProperty('--share', String(share));
      const label = row.querySelector('[data-level-count]');
      if (label) label.textContent = levelCount.toLocaleString(locale);
    }

    const recommendText = this.dataset.recommendText || '';
    const recommended = Number(aggregate.recommendationCount) || 0;
    recommend.hidden = !recommendText || !count || !recommended;
    recommend.textContent = recommendText.replace('[percent]', String(Math.round((recommended / Math.max(count, 1)) * 100)));

    this.removeAttribute('data-loading');
  }

  /** Starts the grid again for the current product and sort. The old cards stay (dimmed) until the new ones arrive. */
  #reload() {
    this.#generation++;
    this.#buffer = [];
    this.#shown = 0;
    this.#nextUrl = `${this.#scopeUrl}/reviews?limit=${FETCH_LIMIT}&orderBy=${encodeURIComponent(this.refs.sort.value || SORTS[0])}`;
    this.refs.list.setAttribute('aria-busy', 'true');
    this.#loadPage(true);
  }

  /** @param {boolean} [fresh] - First page after a reload: replaces the placeholders or the old view's cards */
  async #loadPage(fresh = false) {
    const generation = this.#generation;
    const perPage = Number(this.dataset.perPage) || 12;
    const { more } = this.refs;
    more.disabled = true;

    if (this.dataset.subscriberId) {
      for (let requests = 0; this.#buffer.length < perPage && this.#nextUrl && requests < MAX_REQUESTS; requests++) {
        const data = await this.#fetchJson(this.#nextUrl);
        if (generation !== this.#generation) return;
        this.#nextUrl = data?.nextUrl ? `${API}${data.nextUrl}` : null;
        this.#buffer.push(...this.#qualifying(data?.reviews));
      }
    } else {
      this.#nextUrl = null;
    }

    this.#render(this.#buffer.splice(0, perPage), fresh);
    more.hidden = !this.#buffer.length && !this.#nextUrl;
    more.disabled = false;
  }

  /**
   * Okendo syndicates reviews from every store; keep the storefront's language and, by default, products sold here.
   * @param {OkendoReview[] | undefined} reviews
   * @returns {OkendoReview[]}
   */
  #qualifying(reviews) {
    if (!Array.isArray(reviews)) return [];
    const language = (this.dataset.locale || 'en').split('-')[0].toLowerCase();
    const storeOnly = this.dataset.storeOnly === 'true';
    // "Most recent" leads with the best of the latest; the other sorts still show every review
    const minRating = this.refs.sort.value === SORTS[0] ? MIN_RECENT_RATING : 0;
    return reviews.filter((review) => {
      const reviewLanguage = String(review.languageCode || '').split('-')[0].toLowerCase();
      return (
        String(review.body || '').trim() !== '' &&
        (!reviewLanguage || reviewLanguage === language) &&
        (!storeOnly || Boolean(review.productUrl)) &&
        (Number(review.rating) || 0) >= minRating
      );
    });
  }

  /* ---------- Cards ---------- */

  /**
   * @param {OkendoReview[]} reviews
   * @param {boolean} fresh
   */
  #render(reviews, fresh) {
    const { list, cardTemplate, empty, status } = this.refs;
    if (fresh) list.replaceChildren();

    const locale = this.dataset.locale || undefined;
    const dateFormat = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
    const fragment = document.createDocumentFragment();
    /** @type {HTMLElement[]} */
    const cards = [];

    for (const review of reviews) {
      const card = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
      const rating = Math.round(Number(review.rating) || 0);
      const title = String(review.title || '').trim();

      this.#setText(card, 'title', GENERIC_TITLE.test(title) ? '' : title);
      this.#setText(card, 'body', `“${String(review.body || '').trim()}”`);
      this.#setText(card, 'name', review.reviewer?.displayName);
      this.#setText(card, 'product', review.productName);

      const ratingNode = /** @type {HTMLElement | null} */ (card.querySelector('[data-field="rating"]'));
      if (ratingNode) {
        ratingNode.setAttribute('aria-label', this.#ratingLabel(rating));
        this.#fillStars(ratingNode, rating);
      }

      const date = review.dateCreated ? new Date(review.dateCreated) : null;
      const time = card.querySelector('[data-field="date"]');
      if (time && date && !Number.isNaN(date.getTime())) {
        time.setAttribute('datetime', date.toISOString().slice(0, 10));
        time.textContent = dateFormat.format(date);
      }

      const verified = /** @type {HTMLElement | null} */ (card.querySelector('[data-field="verified"]'));
      if (verified) verified.hidden = !review.reviewer?.isVerified;

      const replyText = this.#plainText(review.reply?.body);
      const reply = /** @type {HTMLElement | null} */ (card.querySelector('[data-field="reply"]'));
      if (reply && replyText) {
        reply.hidden = false;
        this.#setText(card, 'reply-body', replyText);
      }

      const link = card.querySelector('[data-field="product-link"]');
      if (link) {
        if (review.productUrl) link.setAttribute('href', this.#productPath(review.productUrl));
        const image = /** @type {HTMLImageElement | null} */ (link.querySelector('[data-field="product-image"]'));
        if (image && review.productImageUrl) {
          image.src = review.productImageUrl;
          image.hidden = false;
        }
        if (!review.productName) link.remove();
      }

      const item = /** @type {HTMLElement | null} */ (card.firstElementChild);
      if (item) cards.push(item);
      fragment.append(card);
    }

    list.append(fragment);
    list.removeAttribute('aria-busy');
    this.#shown += reviews.length;
    this.#addToggles(cards);

    empty.hidden = this.#shown > 0;
    if (!this.#shown) empty.textContent = this.dataset.emptyText || '';
    status.textContent = (this.dataset.showingText || '[count]').replace('[count]', String(this.#shown));
  }

  /**
   * "Read more" only on cards whose text is cut off. One layout read for the whole batch, after it's in the page.
   * @param {HTMLElement[]} cards
   */
  #addToggles(cards) {
    const clipped = cards.filter((card) => {
      const body = card.querySelector('[data-field="body"]');
      return body ? body.scrollHeight > body.clientHeight + 1 : false;
    });
    for (const card of clipped) {
      const toggle = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-field="toggle"]'));
      if (!toggle) continue;
      toggle.hidden = false;
      toggle.textContent = this.dataset.readMore || '';
      toggle.addEventListener('click', () => {
        const open = card.toggleAttribute('data-expanded');
        toggle.setAttribute('aria-expanded', String(open));
        toggle.textContent = (open ? this.dataset.readLess : this.dataset.readMore) || '';
      });
    }
  }

  /**
   * Okendo's links are protocol-relative to this store; keep the path so they stay on the current domain.
   * @param {string} url
   */
  #productPath(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }

  /**
   * Replies come as HTML from Okendo; only their text is used (never inserted as HTML).
   * @param {string | undefined} html
   */
  #plainText(html) {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Text only (never HTML): review content comes from customers.
   * @param {DocumentFragment} card
   * @param {string} field
   * @param {string | undefined} value
   */
  #setText(card, field, value) {
    const node = card.querySelector(`[data-field="${field}"]`);
    if (node) node.textContent = String(value || '').trim();
  }

  /** @param {number} rating */
  #ratingLabel(rating) {
    return (this.dataset.ratingLabel || '[rating] / 5').replace('[rating]', String(rating));
  }

  /**
   * @param {HTMLElement} container
   * @param {number} filled - Whole stars to fill
   * @param {number} [exact] - The exact rating, for the Nundies star strip (--rating-fill)
   */
  #fillStars(container, filled, exact = filled) {
    container.querySelectorAll('[data-star]').forEach((star) => {
      star.classList.toggle('filled-star', Number(/** @type {HTMLElement} */ (star).dataset.star) <= filled);
    });
    container.style.setProperty('--rating-fill', `${Math.max(0, Math.min(exact, 5)) * 20}%`);
  }
}

if (!customElements.get('reviews-page-component')) {
  customElements.define('reviews-page-component', ReviewsPageComponent);
}
